import ABCyaPhaser from '@abcya/abcya_phaser';
import Phaser from 'phaser';
import SoundAssetKeys from '../SoundAssetKeys';
import MouseTracker from './MouseTracker';
import PumpkinLighting from './PumpkinLighting';
import PaintCanvas from '../common/PaintCanvas';

export default class Pumpkin extends Phaser.GameObjects.Container {
  /**
   * 
   * @param {Phaser.Scene} scene 
   */
  constructor(scene) {
    super(scene);

    // Nighttime display
    this.isLit = false;
    this.pumpkinShadowOverlay = null;

    /** @type {{mt: number[], ct: number[]}[][]} */
    this.cuts = [];
    this.pumpkinID = Phaser.Math.Between(1,3);

    this.pumpkinInside = ABCyaPhaser.Images.addImage(0,0,`pumpkin_inside${this.pumpkinID}`,this).setOrigin(0);

    this.pumpkinLighting = new PumpkinLighting(this.scene, this.pumpkinID);
    this.pumpkinLighting.x = 0;
    this.pumpkinLighting.y = 0;
    this.add(this.pumpkinLighting);

    this.pumpkinOutside = ABCyaPhaser.Images.addImage(0,0,`pumpkin_outside${this.pumpkinID}`,this).setOrigin(0)
      .setInteractive({ draggable: true })
      .disableInteractive();// pumpkin shouldn't be interactive immediately
    this.pumpkinOutside.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, this.startCarving, this);
    this.pumpkinOutside.on(Phaser.Input.Events.GAMEOBJECT_DRAG, this.doCarving, this);
    this.pumpkinOutside.on(Phaser.Input.Events.GAMEOBJECT_DRAG_END, this.stopCarving, this);

    this.workingWidth = this.pumpkinOutside.width;
    this.workingHeight = this.pumpkinOutside.height;

    this.drawSurface = new PaintCanvas(this.scene);
    this.drawSurface.reset(this.workingWidth, this.workingHeight);

    this.mouseTracker = new MouseTracker();

    this.cutFills = this.scene.textures.createCanvas('pumpkin', this.workingWidth, this.workingHeight);

    this.pumpkinMaskCanvas = this.scene.textures.createCanvas('pumpkinMask', this.workingWidth, this.workingHeight);
    this.pumpkinMaskCanvas.context.fillStyle = '#ffffff';
    this.pumpkinMaskCanvas.context.fillRect(0,0,this.workingWidth, this.workingHeight);
    this.pumpkinMaskCanvas.refresh();
    this.pumpkinMaskImage = new Phaser.GameObjects.Image(this.scene, 0,0, 'pumpkinMask').setOrigin(0);
    this.pumpkinMask = new Phaser.Display.Masks.BitmapMask(this.scene, this.pumpkinMaskImage);

    this.outlineDisplayCanvas = this.scene.textures.createCanvas('outlineDisplay', this.workingWidth, this.workingHeight);
    this.outlineDisplay = new Phaser.GameObjects.Image(this.scene, 0,0, this.outlineDisplayCanvas.key).setOrigin(0);

    this.outlineDisplayMaskImage = new Phaser.GameObjects.Image(this.scene, 0,0,ABCyaPhaser.Images.DEFAULT_ATLAS, `pumpkin_inside${this.pumpkinID}`,this).setOrigin(0);
    this.outlineDisplay.setMask(new Phaser.Display.Masks.BitmapMask(this.scene, this.outlineDisplayMaskImage));
    this.add(this.outlineDisplay);
    this.add(this.drawSurface);

    this.pumpkinOutside.setMask(this.pumpkinMask);

    this.pumpkinOutside.alpha = 1;
  }

  startCarving (pointer) {
    if (this.isLit) return;
    this.mouseTracker.reset(pointer.x, pointer.y);
    this.drawSurface.startDrawing(pointer.x, pointer.y);
  }

  doCarving (pointer) {
    if (this.isLit) return;
    this.mouseTracker.addPoint(pointer.x, pointer.y);
    const dist = Phaser.Math.Distance.Between(pointer.x, pointer.y, this.drawSurface.lastDrawPoint.x, this.drawSurface.lastDrawPoint.y);
    if (dist > 3) this.drawSurface.handleDraw(pointer.x, pointer.y);
  }

  stopCarving () {
    if (this.isLit) return;
    this.drawSurface.stopDrawing();
    const coords = this.mouseTracker.coordinates;
    const startpt = { x: coords[coords.length - 1][0], y: coords[coords.length - 1][1] };
    const endpt = this.mouseTracker.lastPoint;
    const dist = Phaser.Math.Distance.BetweenPoints(startpt, endpt);

    if (dist <= 62) {
      ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._cut, this.scene);
      this.addToCutout();
      this.updateMask();
      this.updateOutline();

      this.cuts.push(Phaser.Utils.Objects.DeepCopy(this.drawSurface.drawPoints));
    }
    this.drawSurface.clear();
  }

  addToCutout () {
    const ctx = this.cutFills.context;
    ctx.strokeStyle = '#000099';
    ctx.fillStyle = '#cc0099';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    const dc = this.drawSurface.drawPoints;
    const len = dc.length;
    ctx.beginPath();
    ctx.moveTo(dc[0].mt[0], dc[0].mt[1]);
    for (let i = 0; i < len; i++) {
      ctx.quadraticCurveTo(dc[i].ct[0], dc[i].ct[1], dc[i].ct[2], dc[i].ct[3]);
    }
    ctx.stroke();
    ctx.fill();
    this.cutFills.refresh();
  }

  updateMask () {
    const ctx = this.pumpkinMaskCanvas.context;
    ctx.globalCompositeOperation = 'source-over';
    ctx.fillStyle = 'black';
    ctx.fillRect(0,0,this.workingWidth, this.workingHeight);
    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(this.cutFills.canvas,0, 0,this.workingWidth,this.workingHeight, 0, 0,this.workingWidth, this.workingHeight);
    this.pumpkinMaskCanvas.refresh();
  }

  updateOutline () {
    const ctx = this.outlineDisplayCanvas.context;
    ctx.clearRect(0,0,this.workingWidth, this.workingHeight);
    ctx.globalCompositeOperation = 'source-over';
    const dArr = [-1,-1, 0,-1, 1,-1, -1,0, 1,0, -1,1, 0,1, 1,1]; // offset array
    const s = 3;  // thickness scale
    const x = 0;
    const y = 0;

    // draw images at offsets from the array scaled by s
    for (let i = 0; i < dArr.length; i += 2) {
      ctx.drawImage(this.cutFills.canvas, x + dArr[i] * s, y + dArr[i + 1] * s);
    }

    // fill with color
    ctx.globalCompositeOperation = 'source-in';
    ctx.fillStyle = 'black';
    ctx.fillRect(0,0,this.workingWidth, this.workingHeight);

    ctx.globalCompositeOperation = 'destination-out';
    ctx.drawImage(this.cutFills.canvas, 0, 0, this.workingWidth, this.workingHeight);
    this.outlineDisplayCanvas.refresh();
  }

  newPumpkin (pid) {
    this.pumpkinID = pid;
    this.pumpkinInside.setFrame(`pumpkin_inside${this.pumpkinID}`).setOrigin(0);
    this.outlineDisplayMaskImage.setFrame(`pumpkin_inside${this.pumpkinID}`).setOrigin(0);
    this.pumpkinOutside.setFrame(`pumpkin_outside${this.pumpkinID}`).setOrigin(0)
      .disableInteractive();
    this.workingWidth = this.pumpkinOutside.width;
    this.workingHeight = this.pumpkinOutside.height;
    this.drawSurface.reset(this.workingWidth, this.workingHeight);

    this.pumpkinMaskCanvas.setSize(this.workingWidth, this.workingHeight);
    this.outlineDisplayCanvas.setSize(this.workingWidth, this.workingHeight);
    this.cutFills.setSize(this.workingWidth, this.workingHeight);

    this.resetCuts();

    this.pumpkinMaskCanvas.context.globalCompositeOperation = 'source-over';
    this.pumpkinMaskCanvas.context.fillStyle = '#ffffff';
    this.pumpkinMaskCanvas.context.fillRect(0,0,this.workingWidth, this.workingHeight);
    this.pumpkinMaskCanvas.refresh();

    this.pumpkinLighting.reset(this.pumpkinID);
  }

  enableDrawing() {
    this.pumpkinOutside.setInteractive();
  }

  resetCuts () {
    this.cuts = [];
    this.cutFills.clear();
    this.updateMask();
    this.resetOutline();
  }

  resetOutline () {
    this.outlineDisplayCanvas.clear();
  }

  drawCutouts () {
    const ctx = this.cutFills.context;
    ctx.strokeStyle = '#000099';
    ctx.fillStyle = '#cc0099';
    ctx.lineWidth = 3;
    ctx.lineCap = 'round';

    // stored cuts
    const len = this.cuts.length;
    let pointCnt = 0;
    for (let i = 0; i < len; i++) {
      pointCnt = this.cuts[i].length;
      ctx.beginPath();
      ctx.moveTo(this.cuts[i][0].mt[0], this.cuts[i][0].mt[1]);
      for (let j = 0; j < pointCnt; j++) {
        ctx.quadraticCurveTo(this.cuts[i][j].ct[0], this.cuts[i][j].ct[1], this.cuts[i][j].ct[2], this.cuts[i][j].ct[3]);
      }
      ctx.stroke();
      ctx.fill();
    }

    this.cutFills.refresh();
  }

  undoCut () {
    if (this.cuts.length <= 0) return;
    ABCyaPhaser.Audio.playOneShot(SoundAssetKeys._undo, this.scene);
    this.cuts.pop();
    this.cutFills.clear();
    this.drawCutouts();
    this.updateMask();
    this.resetOutline();
    this.updateOutline();
  }

  turnDark () {
    this.pumpkinOutside.disableInteractive();
    this.isLit = true;
    this.pumpkinOutside.setFrame(`Pumpkin_${this.pumpkinID}_shadow`);

    this.drawSurface.setVisible(false);

    this.pumpkinLighting.resetDark();
  }

  turnDaytime () {
    this.pumpkinOutside.setInteractive();
    this.isLit = false;
    this.pumpkinOutside.setFrame(`pumpkin_outside${this.pumpkinID}`);

    this.pumpkinLighting.hide();

    this.drawSurface.setVisible(true);
  }

  toggleCandle () {
    return this.pumpkinLighting.toggleCandle();
  }

  tick (dt) {
    if (this.isLit) {
      this.pumpkinLighting.tick(dt);
    }

    this.pumpkinMaskImage.setPosition(this.x, this.y);
    this.outlineDisplayMaskImage.setPosition(this.x, this.y);
  }
}